import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, extractApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { ArrowLeft, Check, ShieldCheck, Lock, ArrowRight } from "@phosphor-icons/react";

function formatLocal(amount, currency) {
  if (amount == null) return "—";
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: currency || "XOF",
      maximumFractionDigits: ["XOF", "NGN", "KES"].includes(currency || "XOF") ? 0 : 2,
    }).format(amount);
  } catch {
    return `${amount} ${currency || ""}`;
  }
}

export default function Subscribe() {
  const [params] = useSearchParams();
  const requestedPlan = params.get("plan") || "professional";
  const { user } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(requestedPlan);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscription/plans");
        setConfig(data);
        // Force fall-back if requested plan is not billable
        const buyables = (data.plans || []).filter((p) => !p.contact_only && p.price > 0);
        if (!buyables.find((p) => p.key === requestedPlan)) {
          setSelected(buyables[0]?.key || "starter");
        }
      } finally { setLoading(false); }
    })();
  }, [requestedPlan]);

  const startCheckout = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const cb = `${window.location.origin}/payment/callback`;
      const { data } = await api.post("/subscription/initialize", { plan_key: selected, callback_url: cb });
      if (!data?.authorization_url) throw new Error("No authorization URL");
      window.location.href = data.authorization_url;
    } catch (err) {
      toast.error(extractApiError(err, "Could not start checkout."));
      setSubmitting(false);
    }
  };

  if (loading || !config) return <div className="min-h-screen grid place-items-center text-sm text-muted-foreground">Loading pricing…</div>;

  const buyables = (config.plans || []).filter((p) => !p.contact_only && p.price > 0);
  const active = buyables.find((p) => p.key === selected) || buyables[0];

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="subscribe-page">
      <header className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display text-base">O</div>
            <div className="font-display text-xl tracking-tight">ORIXA</div>
          </Link>
          <Link to="/pricing" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
            <ArrowLeft size={14} /> Back to pricing
          </Link>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-10">
        <div className="lg:col-span-3">
          <div className="text-overline text-primary mb-3">Subscribe</div>
          <h1 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">Activate your Orixa Invest workspace</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Secure payments powered by Paystack · billed in {config.currency} · pay by card, bank transfer or mobile money.
          </p>

          <div className="mt-8 space-y-3">
            {buyables.map((p) => (
              <button
                key={p.key}
                onClick={() => setSelected(p.key)}
                className={`w-full text-left border rounded-xl p-5 transition-all ${
                  selected === p.key ? "border-primary bg-teal-soft ring-2 ring-primary/15" : "border-border bg-card hover:border-primary/40"
                }`}
                data-testid={`sub-plan-${p.key}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <div className="font-display text-lg tracking-tight">{p.label}</div>
                      {p.recommended && <span className="text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground rounded-full px-2 py-0.5">Recommended</span>}
                    </div>
                    <div className="mt-1 text-xs text-muted-foreground">{p.tagline}</div>
                  </div>
                  <div className="text-right whitespace-nowrap">
                    <div className="text-2xl font-display text-primary">{formatLocal(p.local_price, p.currency)}</div>
                    <div className="text-[11px] text-muted-foreground">{Number(p.price).toLocaleString('fr-FR')} XOF/mois</div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          <button
            onClick={startCheckout}
            disabled={submitting || !active}
            className="mt-8 w-full bg-primary text-primary-foreground rounded-lg py-3.5 font-semibold hover:bg-primary/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors card-elev inline-flex items-center justify-center gap-2"
            data-testid="sub-checkout"
          >
            <Lock size={16} weight="bold" />
            {submitting ? "Opening secure checkout…" : `Continue with ${active?.label || "Starter"} — ${formatLocal(active?.local_price, active?.currency)}/mo`}
          </button>

          <div className="mt-4 flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
            <div className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="bold" className="text-teal" /> SSL secured</div>
            <div className="h-3 w-px bg-border" />
            <div>Secure payments powered by Paystack</div>
          </div>

          <p className="mt-6 text-xs text-muted-foreground leading-relaxed">
            By subscribing, you agree to our{" "}
            <Link to="/legal/terms" className="text-primary underline underline-offset-4">Terms</Link>,{" "}
            <Link to="/legal/privacy" className="text-primary underline underline-offset-4">Privacy Policy</Link> and{" "}
            <Link to="/legal/refund" className="text-primary underline underline-offset-4">Refund Policy</Link>.
          </p>
        </div>

        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-2xl p-6 sm:p-7 card-elev lg:sticky lg:top-24">
            <div className="text-overline text-muted-foreground mb-3">Order summary</div>
            <div className="font-display text-2xl tracking-tight">Orixa {active?.label || "—"}</div>
            <div className="mt-1 text-sm text-muted-foreground">Monthly subscription · billed in {active?.currency}</div>
            <div className="mt-5 flex items-end justify-between border-y border-border py-4">
              <span className="text-sm font-medium">Total today</span>
              <span className="data-large text-3xl text-primary">{formatLocal(active?.local_price, active?.currency)}<span className="text-sm text-muted-foreground font-normal">/mo</span></span>
            </div>
            <ul className="mt-5 space-y-2.5 text-sm">
              {(active?.features || []).map((f) => (
                <li key={f} className="flex items-start gap-2.5">
                  <div className="h-5 w-5 rounded-full bg-teal-soft text-teal grid place-items-center flex-shrink-0 mt-0.5">
                    <Check size={11} weight="bold" />
                  </div>
                  <span className="leading-snug">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-5 border-t border-border text-xs text-muted-foreground leading-relaxed">
              Signed in as <strong>{user?.email || "…"}</strong>. Your workspace will be upgraded immediately after payment.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
